// packages/web/src/components/service/ServiceConfiguration.tsx
// Configuration component for service variants and frequencies

import { Box, Typography, Chip } from '@mui/material';
import React from 'react';

import { ServiceVariantGrid } from './ServiceVariantGrid';
import { useTermSelection } from '../../hooks/useTermSelection';
import { MarketplaceService } from '../../types/pricing';
import { TermTabs } from '../marketplace/TermTabs';

interface ServiceConfigurationProps {
  service: MarketplaceService;
  selectedVariant: string | undefined;
  selectedFrequency: string | undefined;
  onVariantChange: (variant: string | undefined) => void;
  onFrequencyChange: (frequency: string | undefined) => void;
  selectedTerm?: number | null;
  onTermChange?: (term: number) => void;
}

const ServiceConfiguration: React.FC<ServiceConfigurationProps> = ({
  service,
  selectedVariant,
  selectedFrequency,
  onVariantChange,
  onFrequencyChange,
  selectedTerm,
  onTermChange
}) => {

  // Use term selection hook if not provided
  const { selectedTerm: internalSelectedTerm, handleTermChange: internalHandleTermChange } = useTermSelection(service);
  const effectiveSelectedTerm = selectedTerm !== undefined ? selectedTerm : internalSelectedTerm;
  const effectiveOnTermChange = onTermChange || internalHandleTermChange;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Configure Your Service
      </Typography>

      {/* Term Tabs for Contract Services */}
      <TermTabs
        service={service}
        selectedTerm={effectiveSelectedTerm}
        onTermChange={effectiveOnTermChange}
      />

      <ServiceVariantGrid
        service={service}
        selectedVariant={selectedVariant}
        selectedFrequency={selectedFrequency}
        selectedTerm={effectiveSelectedTerm}
        onVariantChange={onVariantChange}
        onFrequencyChange={onFrequencyChange}
      />

      {/* Selected Options Summary */}
      {(selectedVariant || selectedFrequency) && (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Selected Options:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {selectedVariant && (
              <Chip
                label={`Variant: ${selectedVariant}`}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
            {selectedFrequency && (
              <Chip
                label={`Frequency: ${selectedFrequency}`}
                size="small"
                color="secondary"
                variant="outlined"
              />
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default React.memo(ServiceConfiguration);
